/**
 * English strings of Cài đặt › Module (ADR-025 §8): the integration modules of every business
 * branch, their connections (instances), settings, tests, callbacks, logs and actions. The
 * Vietnamese file has exactly the same keys. Every code the server shows has a name here
 * (tests/module-labels.test.mjs reads the codes from the Rust sources).
 */
export const modules = {
  page: {
    title: 'Modules',
    subtitle:
      'Every outside service the platform works with, by business area. Add a connection, fill in its settings, test it, then switch it on; on the production server a second person approves each change.',
    areas: 'Business areas',
    skipAreas: 'Skip to the open area',
    loadFailed: 'Could not load the modules',
    backTo: 'Back to {{branch}}',
  },
  branch: {
    registry: 'Domain names',
    payment: 'Payments',
    notify: 'Notifications',
    einvoice: 'E-invoices',
    captcha: 'Bot protection',
    signing: 'Digital signing',
    ekyc: 'eKYC',
    ocr: 'Document reading (OCR)',
    regulatory: 'VNNIC reporting',
    lookup: 'Domain lookup',
    escrow: 'Data escrow',
    dns: 'DNS',
    hosting: 'Hosting',
    email: 'Business email',
    cloud: 'Cloud, VPS',
    website: 'Website',
    addons: 'Add-ons',
  },
  branchHint: {
    registry: 'Registries and partner registrars: domain registrations, renewals and transfers.',
    payment: 'Payment providers (SePay, Casso, TinoPay, VNPAY…): bank-transfer QR codes, notices of incoming money, matched with orders and invoices.',
    notify: 'E-mails and Zalo messages to customers and staff.',
    einvoice: 'Electronic invoices issued through an e-invoice provider.',
    captcha: 'The "not a robot" check of the customer sign-in and sign-up forms.',
    signing: "The registrar's digital signature on domain declarations and files.",
    ekyc: 'Identity checks of registrants: identity documents and faces.',
    ocr: 'Reading identity documents and declarations, on our own servers only.',
    regulatory: 'Reports and domain dossiers sent to VNNIC.',
    lookup: 'Whether a domain name is free and who holds it (WHOIS).',
    escrow: 'Copies of registration data deposited with an escrow agent.',
    dns: 'DNS hosting for customer domains.',
    hosting: 'Web hosting accounts.',
    email: 'Business e-mail accounts.',
    cloud: 'Cloud servers and VPS.',
    website: 'Website builders and managed websites.',
    addons: 'Extra features installed on the platform.',
  },
  branchTab: {
    modules: 'Modules',
    noModules: 'No module yet',
    noModulesHint: "This area is not connected to any provider yet. When a provider's module is added, it appears here, ready for a connection.",
    coreSettings: 'Settings of the whole area',
    coreSettingsHint: 'Rules of the branch itself, shared by all its modules; changed and approved like any setting.',
    coreEdit: 'Edit',
    coreDefaults: 'Nothing saved yet: the defaults are in use.',
    coreValues: 'Show the values in force',
    summary_one: '{{count}} module',
    summary_other: '{{count}} modules',
    summaryOn_one: '{{on}} of {{count}} connection on',
    summaryOn_other: '{{on}} of {{count}} connections on',
    legacyTitle: 'Connections configured on their own page',
    legacyHint: 'These services are not modules yet. They keep their own settings page until they become modules.',
    legacyConfigured: 'Configured (version {{version}})',
    legacyNotConfigured: 'Not configured in the Control Center (server settings)',
    openPage: 'Open its settings page',
    connectionsTitle: 'Registry connections',
    connectionsHint: 'Each connection has its own page with addresses, sign-in details, certificates and the TLDs it registers.',
    connectionsNone: 'No registry connection yet.',
    connectionColumns: { name: 'Connection', kind: 'Type', protocol: 'How it connects', module: 'Module', status: 'Status' },
  },
  card: {
    vendor: 'Provider',
    version: 'Module version {{version}}',
    versionShort: 'v{{version}}',
    capabilities: 'What it can do',
    noCapabilities: 'What every module of this area does.',
    documents: 'Provider documentation',
    filedDocument: 'Filed with the platform: {{path}}',
    instances: 'Connections',
    noInstances: 'No connection yet: add one to use this module.',
    add: 'Add a connection',
    limit_one: '{{used}} of max {{count}} connection',
    limit_other: '{{used}} of max {{count}} connections',
    open: 'Open',
    switchOn: 'Switch on',
    switchOff: 'Switch off',
    notProductionReady: 'For development and tests only: it cannot be switched on on the production server.',
    versionInForce: 'Version {{version}} in force',
    noVersion: 'Nothing in force',
  },
  instanceColumns: { name: 'Connection', status: 'Status', health: 'Health', test: 'Last test', actions: '' },
  status: {
    NEW: 'Not set up',
    DRAFT: 'Draft',
    TESTED: 'Draft tested',
    PENDING: 'Waiting for approval',
    ACTIVE: 'On',
    DISABLED: 'Off',
  },
  statusHint: {
    NEW: 'Nothing has been saved yet.',
    DRAFT: 'A draft is saved; test it before switching it on.',
    TESTED: 'The draft passed the connection test within the last 24 hours and can be applied.',
    PENDING: 'A change waits for a second person to approve it.',
    ACTIVE: 'The connection is in use.',
    DISABLED: 'The connection is switched off.',
  },
  health: {
    healthy: 'Working',
    degraded: 'Errors',
    down: 'Not working',
    unknown: 'No data yet',
  },
  healthHint: {
    healthy: 'The last test passed and recent calls succeed.',
    degraded: 'The last test failed or many recent calls failed.',
    down: 'A service could not load this connection.',
    unknown: 'Switched off, or no test and no call yet.',
  },
  versionStatus: {
    DRAFT: 'Draft',
    PENDING_APPROVAL: 'Waiting for approval',
    ACTIVE: 'In force',
    SUPERSEDED: 'Replaced',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
  },
  instance: {
    title: '{{module}} · {{instance}}',
    coreTitle: 'Settings of the branch {{branch}}',
    defaultName: 'Main connection',
    loadFailed: 'Could not load this connection',
    tabs: {
      overview: 'Overview',
      config: 'Settings',
      test: 'Connection test',
      callback: 'Callback',
      logs: 'Logs',
      actions: 'Actions',
      pages: 'Business pages',
    },
    overview: {
      state: 'State',
      enabled: 'Switched on',
      generation: 'Configuration generation',
      inForce: 'Version in force',
      noneInForce: 'No version in force yet',
      activatedBy: 'Applied by {{who}}',
      displayName: 'Name',
      key: 'Connection key',
      applied: 'Services using it',
      appliedHint: 'Services that loaded this connection in the last 10 minutes.',
      appliedNone: 'No service has loaded it in the last 10 minutes.',
      upToDate: 'Up to date',
      formerSubject: 'former name',
      formerSubjectHint: 'This service reports under the connection’s former name ({{subject}}), from before the switch to modules.',
      behind: 'Older configuration',
      module: 'About the module',
      vendor: 'Provider',
      moduleVersion: 'Module version',
      data: 'Data it handles',
      egress: 'Where it connects',
      disable: 'Switching it off',
      instances: 'Connections allowed',
      testPolicy: 'Connection test',
      jobs: 'Scheduled work',
      events: 'Events it reports',
      noJobs: 'None',
      everySeconds: 'every {{count}} seconds',
      everyMinutes: 'every {{count}} minutes',
      everyHours: 'every {{count}} hours',
      dailyAt: 'every day at the time of "{{setting}}" (default {{time}})',
    },
    source: {
      DATABASE: 'Settings from the Control Center',
      ENVIRONMENT: 'Server settings (environment variables)',
      DISABLED: 'Switched off',
    },
    loadError: {
      RESTART_REQUIRED: 'Restart needed to use the new settings',
      SECRET_UNAVAILABLE: 'A secret could not be read',
      CONFIGURATION_INVALID: 'The settings were refused by the module',
      SETTINGS_UNAVAILABLE: 'The settings could not be read',
      other: 'Could not load the settings',
    },
  },
  data: { public: 'Public data only', personal: 'Personal data', regulated: 'Regulated data (VNNIC, identity)' },
  egress: {
    hosts: 'Only these addresses: {{hosts}}',
    from_setting: 'Only the server its connection names ({{setting}})',
    local_only: 'Nowhere: runs on our own servers',
    none: 'Nowhere',
  },
  disable: {
    immediate: 'At once, by one person',
    needs_approval: 'Needs a second person on the production server (it removes a protection)',
  },
  instancesPolicy: { one: 'One', many: 'Up to {{max}}' },
  testPolicy: { steps: 'Steps: {{steps}}', no_test: 'None: {{reason}}' },
  config: {
    inForce: 'In force',
    draft: 'Draft',
    pending: 'Waiting for approval',
    nothing: 'Nothing saved yet. Fill in the settings to create a draft.',
    edit: 'Edit settings',
    create: 'Fill in the settings',
    test: 'Test this draft',
    propose: 'Apply this draft',
    proposeProduction: 'Send for approval',
    proposeNeedsTest: 'Run the connection test of this draft first (a passing test within the last 24 hours).',
    tested: 'Tested within the last 24 hours',
    notTested: 'Not tested yet',
    noTestNeeded: 'This module has no connection test',
    secrets: 'Secrets',
    reason: 'Reason',
    savedBy: 'Saved by {{who}}',
    history: 'Version history',
    historyNone: 'No version yet.',
    newer: 'Newer',
    older: 'Older',
    columns: { version: 'Version', status: 'Status', enabled: 'On', values: 'Settings', reason: 'Reason', by: 'By' },
    approval: 'Approval',
    enabledField: 'Switched on',
    unset: 'Not set (default)',
    advanced: 'Advanced',
    group: { other: 'Other settings' },
  },
  form: {
    title: 'Settings of {{name}}',
    description: 'Saving makes a draft. Nothing changes for customers until the draft is tested and applied.',
    enabled: 'Switch the connection on',
    enabledHint: 'Off: the platform does not use this connection.',
    displayName: 'Name shown to staff',
    displayNameHint: 'Optional, e.g. the bank account it reads.',
    required: 'Required',
    requiredWhenOn: 'Required when switched on',
    optional: 'Optional',
    defaultValue: 'Default: {{value}}',
    useDefault: 'Use the default',
    maxLength: 'At most {{count}} characters',
    pattern: 'Must have the expected form',
    httpsOnly: 'Only https:// addresses',
    range: 'From {{min}} to {{max}}',
    timeHint: 'Vietnam time (HH:MM)',
    phoneHint: 'Mobile number with country code, without +, e.g. 84912345678',
    pathHint: 'A path that starts with /, e.g. /api/v1/list',
    emailHint: 'An e-mail address',
    certificateHint: 'A certificate file (PEM). The private key is never shown again.',
    certificateWithKey: 'A certificate with its private key (PEM). The key is never shown again.',
    fileHint: 'Accepted files: {{types}}; at most {{size}}.',
    fileTooBig: 'The file is larger than {{size}}.',
    fileRead: 'Read {{name}} ({{size}}). The content is not displayed.',
    choose: 'Choose…',
    loadOptions: 'Load the choices from the provider',
    optionsUnavailable: 'The choices can be loaded once the connection is switched on; type the value meanwhile.',
    secretPermission: 'Changing a secret needs the permission to manage credentials.',
    keep: 'Keep the current value',
    saveDraft: 'Save the draft',
    cancel: 'Cancel',
    close: 'Close',
    saved: 'Draft saved (version {{version}}).',
    nextSteps: 'Next: run the connection test of this draft, then apply it.',
    nextStepsNoTest: 'Next: apply the draft.',
    errors: {
      REQUIRED: 'Required',
      TOO_LONG: 'Too long',
      PATTERN: 'Not in the expected form',
      NOT_IN_OPTIONS: 'Not one of the choices',
      HTTPS_REQUIRED: 'Must start with https://',
      INVALID_URL: 'Not a valid web address',
      OUT_OF_RANGE: 'Out of range',
      INVALID_TIME: 'Not a time (HH:MM)',
      INVALID_EMAIL: 'Not an e-mail address',
      INVALID_PHONE: 'Not a phone number with country code',
      INVALID_PATH: 'Not a path starting with /',
      NOT_A_NUMBER: 'Not a whole number',
    },
  },
  fieldType: {
    text: 'Text',
    secret: 'Secret (write-only)',
    select: 'One choice',
    multi_select: 'Several choices',
    url: 'Web address',
    path: 'Path',
    integer: 'Number',
    boolean: 'On / off',
    time_of_day: 'Time of day',
    email: 'E-mail address',
    phone: 'Phone number',
    certificate: 'Certificate',
    file: 'File',
  },
  unit: { ms: 'ms', seconds: 'seconds', minutes: 'minutes', vnd: 'VND', records: 'records', attempts: 'attempts', percent: '%' },
  propose: {
    title: 'Apply version {{version}}',
    description: 'The tested draft replaces the version in force.',
    descriptionProduction: 'The draft is sent to a second person, who approves it; then it is applied.',
    changes: 'What changes',
    noChanges: 'Nothing changes compared with the version in force.',
  },
  toggle: {
    offTitle: 'Switch off {{name}}',
    onTitle: 'Switch on {{name}}',
    offDescription: 'The platform stops using this connection.',
    offApproval: 'On the production server switching this module off needs a second person: it removes a protection.',
    onDescription: 'The last version is switched on again. If it has not passed a test within the last 24 hours, it is saved as a draft to test first.',
    testRequired: 'Saved as a draft: run the connection test, then apply it.',
  },
  create: {
    title: 'Add a connection to {{module}}',
    description: 'A connection has its own settings, secrets, test and logs. A module that allows several connections can serve, for example, several bank accounts.',
    key: 'Connection key',
    keyHint: 'Lower-case letters, digits and "-", e.g. default, acb-2. It is part of the callback address and cannot be changed.',
    displayName: 'Name shown to staff',
    created: 'Connection {{key}} added. Fill in its settings next.',
    open: 'Open the connection',
  },
  test: {
    title: 'Connection test',
    hint: 'The test uses the chosen version without applying it. One test per connection every 10 seconds.',
    run: 'Run the test',
    running: 'Testing…',
    version: 'Version to test',
    versionOf: {
      draft: 'Draft (version {{version}})',
      pending: 'Waiting for approval (version {{version}})',
      active: 'In force (version {{version}})',
    },
    nothingToTest: 'Save a draft first.',
    none: 'No test yet.',
    history: 'Recent tests',
    outcome: { OK: 'Passed', FAILED: 'Failed' },
    stepOutcome: { OK: 'Passed', FAILED: 'Failed', SKIPPED: 'Not run' },
    step: {
      REACHABILITY: 'Reaching the provider',
      TOKEN: 'Getting an access token',
      API_CALL: 'A read-only call',
      SIGNATURE: 'Checking signatures',
      SEND_TO_ADMIN: 'A message to the administrator',
    },
    forVersion: 'Version {{version}}',
    duration: '{{ms}} ms',
    rateLimited: 'Wait {{seconds}} seconds before testing again.',
  },
  failure: {
    TIMEOUT: 'No answer in time',
    DNS: 'The address could not be found',
    CONNECT: 'Could not connect',
    TLS: 'Secure connection failed (certificate)',
    BAD_ANSWER: 'The answer could not be read',
    CONFIGURATION: 'The settings are incomplete or wrong',
    RATE_LIMITED: 'Too many requests: the provider asks to wait',
    EGRESS_REFUSED: 'The address is not allowed for this module',
    PROVIDER_ERROR: 'The provider had an error',
  },
  refusal: {
    NOT_FOUND: 'Not found at the provider',
    ALREADY_EXISTS: 'Already exists at the provider',
    UNAUTHORIZED: 'The provider refused the credentials',
    IP_NOT_ALLOWED: "The provider does not accept requests from this server's address",
    INVALID_REQUEST: 'The provider refused the request as invalid',
    CONFLICT: 'The provider refused it: it conflicts with the current state',
    QUOTA_EXCEEDED: 'Quota used up at the provider',
    INSUFFICIENT_FUNDS: 'Not enough balance at the provider',
    RATE_LIMITED: 'Too many requests: the provider asks to wait',
    MAINTENANCE: 'The provider is under maintenance',
    UNSUPPORTED: 'The provider does not offer this',
  },
  otherCode: 'Other answer from the provider',
  callback: {
    title: 'Callback address',
    none: 'This module receives no callbacks from its provider.',
    path: 'Address to enter at the provider',
    pathHint: "Put the public address of the platform's API in front of this path, e.g. https://api.example.vn{{path}}.",
    limits: 'The provider must send at most {{size}} per call; the platform answers within {{seconds}} seconds.',
    security: 'Every call is checked (signature or key) before anything is read; calls that fail the check are refused and not stored.',
    method: 'The provider calls with POST, its data in the request body.',
    methodGet: 'The provider calls with GET, its data in the address (after the ?); POST is accepted too.',
    setup: 'How to set it up at the provider',
    receipts: 'Calls received',
    receiptsHint: 'Checked calls only; their content is never stored, only a fingerprint and the references they carried.',
    receiptsNone: 'No call received yet.',
    receiptOutcome: { PROCESSED: 'Processed', DUPLICATE: 'Already received', MALFORMED: 'Could not be read', FAILED: 'Failed' },
    columns: { at: 'Received', outcome: 'Result', ids: 'References', size: 'Size', status: 'Answer', duration: 'Time' },
  },
  logs: {
    calls: 'Calls to the provider',
    callsHint: 'Each call the platform made with this connection, without its content.',
    callsNone: 'No call yet.',
    callOutcome: { ok: 'Succeeded', refused: 'Refused by the provider', not_sent: 'Not sent', unknown: 'Result unknown' },
    columns: { at: 'Time', operation: 'Operation', outcome: 'Result', code: 'Code', duration: 'Time taken', version: 'Module version' },
    operation: { test: 'Connection test', action: 'Action: {{name}}', other: 'Operation' },
    audit: 'Audit log of this connection',
    more: 'Older',
    newest: 'Newest',
  },
  actions: {
    title: 'Actions',
    hint: 'Actions the module offers on the connection in force. Actions that change something are recorded in the audit log.',
    none: 'This module offers no action.',
    run: 'Run',
    running: 'Running…',
    readOnly: 'Reads only',
    changes: 'Changes something',
    needs: 'Needs: {{permission}}',
    needsOn: 'Available once the connection is switched on.',
    result: 'Result',
    outcome: { ok: 'Done', refused: 'Refused by the provider', not_sent: 'Not sent', unknown: 'Result unknown' },
    empty: 'No data',
  },
  pages: {
    title: 'Business pages',
    hint: 'Pages of the platform that use this module.',
    none: 'This module has no page of its own.',
    open: 'Open',
    missing: 'This page is not available in this version of the Control Center.',
  },
  hosting: {
    intro: "What this connection's panel holds: its servers and their room, the accounts it holds (with the ones not in the system yet) and its packages. Every read of the panel is a line of Nhật ký.",
    servers: 'Servers',
    serversHint: "As placement sees them: the panel's last report and the accounts placed since. A server that takes no new account keeps its accounts.",
    serversNone: 'No server reported yet: refresh from the panel.',
    serverCol: { server: 'Server', accounts: 'Accounts', disk: 'Free disk', up: 'Up', accepting: 'New accounts', checked: 'Last report', note: 'Note' },
    accountsOf: '{{accounts}} of {{max}}',
    pendingOf: '+{{count}} placed since',
    diskGb: '{{value}} GB',
    upYes: 'Up',
    upNo: 'Down',
    gone: 'No longer reported',
    acceptingYes: 'Accepted',
    acceptingNo: 'Stopped',
    refresh: 'Refresh from the panel',
    refreshTitle: 'Ask the panel for its servers',
    refreshIntro: 'Reads the servers and their room at the panel now (the system does it every 15 minutes).',
    refreshDone: 'Servers read from the panel.',
    stop: 'Stop new accounts',
    start: 'Accept new accounts',
    stopTitle: 'Stop new accounts on {{server}}',
    startTitle: 'Accept new accounts on {{server}}',
    acceptingIntro: "New accounts go to the least loaded server that accepts them; this server's accounts stay where they are.",
    note: 'Note (shown on the server)',
    acceptingDone: 'Saved.',
    accounts: 'Accounts on the panel',
    accountsHint: 'Read at the panel when you ask, a page at a time. "Not in the system" is an account the panel holds that no service of ours is.',
    accountCol: { username: 'Username', domain: 'Domain', status: 'At the panel', plan: 'Package', service: 'In the system', customer: 'Customer' },
    status: { PROVISIONING: 'Being created', ACTIVE: 'Running', SUSPENDED: 'Suspended', TERMINATED: 'Removed', FAILED: 'Failed', UNKNOWN: 'Unknown' },
    notInSystem: 'Not in the system',
    openService: 'Open the service',
    load: 'Read from the panel',
    more: 'Next page',
    packages: 'Packages on the panel',
    packagesHint: "What a plan's provider plan may name (Sản phẩm & giá › plan › Cấp phát).",
    loadPackages: 'Read from the panel',
    packageCol: { code: 'Plan code', name: 'Package', limits: 'Limits' },
    meter: { DISK_MB: 'Disk (MB)', BANDWIDTH_MB: 'Bandwidth (MB)', DATABASES: 'Databases', MAILBOXES: 'Mailboxes' },
    unlimited: 'no limit',
  },
  capability: {
    'addons.admin': 'Pages for staff',
    'addons.api': 'Its own API routes',
    'addons.client_area': 'Pages for customers',
    'addons.events': 'Reacts to platform events',
    'addons.jobs': 'Scheduled work',
    'addons.storage': 'Its own data storage',
    'captcha.risk': 'Risk score',
    'cloud.backups': 'Backups',
    'cloud.console': 'Server console',
    'cloud.credentials': 'Access details',
    'cloud.firewalls': 'Firewalls',
    'cloud.inventory': 'Lists existing servers',
    'cloud.ips': 'Extra IP addresses',
    'cloud.plans': 'Plans and prices',
    'cloud.renewals': 'Renewals at the provider',
    'cloud.reverse_dns': 'Reverse DNS',
    'cloud.snapshots': 'Snapshots',
    'cloud.ssh_keys': 'SSH keys',
    'cloud.sso': 'Sign-in without a password',
    'cloud.usage': 'Usage',
    'cloud.volumes': 'Extra disks',
    'dns.credentials': 'Access details',
    'dns.dnssec': 'DNSSEC',
    'dns.inventory': 'Lists existing zones',
    'dns.native_templates': 'Provider templates',
    'dns.plans': 'Plans and prices',
    'dns.propagation': 'Propagation check',
    'dns.renewals': 'Renewals at the provider',
    'dns.secondary': 'Secondary DNS',
    'dns.sso': 'Sign-in without a password',
    'dns.stats': 'Query statistics',
    'dns.usage': 'Usage',
    'dns.zone_files': 'Zone files',
    'einvoice.adjust': 'Adjusting invoices',
    'einvoice.buyer_lookup': 'Buyer lookup by tax code',
    'einvoice.cancel': 'Cancelling invoices',
    'einvoice.delivery': 'Sending invoices to buyers',
    'einvoice.drafts': 'Draft invoices',
    'einvoice.payment_marking': 'Marking invoices paid',
    'einvoice.replace': 'Replacing invoices',
    'ekyc.chip': 'Reading the ID card chip',
    'ekyc.liveness': 'Live face check',
    'email.aliases': 'Aliases',
    'email.credentials': 'Access details',
    'email.dkim': 'DKIM signing',
    'email.domain_aliases': 'Domain aliases',
    'email.inventory': 'Lists existing mailboxes',
    'email.licences': 'Licences',
    'email.migration': 'Mailbox migration',
    'email.plans': 'Plans and prices',
    'email.renewals': 'Renewals at the provider',
    'email.sso': 'Sign-in without a password',
    'email.usage': 'Usage',
    'hosting.backups': 'Backups and restores',
    'hosting.databases': 'Databases',
    'hosting.domains': 'Domains on the account',
    'hosting.ftp': 'FTP accounts',
    'hosting.placement': 'Choice of server by free capacity',
    'hosting.ssl': 'SSL certificates',
    'hosting.credentials': 'Access details',
    'hosting.inventory': 'Lists existing accounts',
    'hosting.plans': 'Plans and prices',
    'hosting.renewals': 'Renewals at the provider',
    'hosting.sso': 'Sign-in without a password',
    'hosting.usage': 'Usage',
    'notify.inbound': 'Receives replies',
    'notify.quota': 'Shows the sending quota',
    'notify.receipts': 'Delivery reports',
    'notify.templates': 'Provider templates',
    'ocr.pdf': 'Reads PDF files',
    'payment.accounts': 'Lists the linked accounts',
    'payment.bank_account': 'Receiving account (transfer QR)',
    'payment.history': 'Reads past transactions to reconcile',
    'payment.ipn': 'Notice of incoming money (IPN/webhook)',
    'payment.redirect': "The gateway's payment page",
    'payment.refund': 'Refund to the source',
    'payment.status': 'Status query',
    'regulatory.business_registry': 'Business registry lookup',
    'regulatory.change_reports': 'Change reports',
    'regulatory.daily_tracking': 'Daily tracking report',
    'regulatory.deposit': 'Deposit balance',
    'regulatory.dossier_sync': 'Domain dossier sync',
    'regulatory.maintained_list': 'Maintained domain list',
    'regulatory.places': 'Administrative places',
    'regulatory.promotion_codes': 'Promotion codes',
    'signing.callbacks': 'Receives signing results the provider reports back',
    'signing.hash_signing': 'Signs a hash of the file',
    'website.backups': 'Backups',
    'website.cms': 'Content management',
    'website.credentials': 'Access details',
    'website.editor': 'Site editor',
    'website.inventory': 'Lists existing websites',
    'website.plans': 'Plans and prices',
    'website.renewals': 'Renewals at the provider',
    'website.ssl': 'SSL certificates',
    'website.sso': 'Sign-in without a password',
    'website.staging': 'Staging copy',
    'website.usage': 'Usage',
    other: 'Other ({{code}})',
  },
  errors: {
    failed: 'The change could not be made',
    code: {
      UNKNOWN_BRANCH: 'This business area does not exist.',
      UNKNOWN_MODULE: 'This module is not installed on this server.',
      UNKNOWN_INSTANCE: 'This connection does not exist (it may have been removed).',
      UNKNOWN_ACTION: 'The module offers no such action.',
      UNKNOWN_VERSION: 'This version does not exist.',
      INSTANCE_EXISTS: 'A connection with this key already exists.',
      INSTANCE_LIMIT: 'This module allows no more connections.',
      NOT_A_DRAFT: 'Only the current draft can be applied. Reload the page.',
      TEST_REQUIRED: 'Run the connection test of this draft first (a passing test within the last 24 hours).',
      NO_TEST: 'This module has no connection test.',
      NOT_CONFIGURED: 'Save a draft first.',
      NOT_ACTIVE: 'The connection is not switched on.',
      ALREADY_DISABLED: 'The connection is already switched off.',
      ALREADY_ENABLED: 'The connection is already switched on.',
      PENDING_EXISTS: 'A change of this connection already waits for approval.',
      PENDING_CHANGE_EXISTS: 'A change of this connection already waits for approval.',
      CONCURRENT_CHANGE: 'Someone else changed this connection meanwhile. Reload the page.',
      TEST_RATE_LIMITED: 'Wait a few seconds before testing again.',
      NOT_A_SWITCH: 'The settings of a branch are not switched on or off.',
      SCHEDULE_FAILED: 'The scheduled work of the module could not be planned.',
      VALIDATION_FAILED: 'Some settings are not valid:',
      other: 'The server refused the request.',
    },
    field: {
      REQUIRED: 'Required',
      UNKNOWN_FIELD: 'Not a setting of this module',
      NOT_TEXT: 'Must be text',
      TOO_LONG: 'Too long',
      PATTERN: 'Not in the expected form',
      NOT_IN_OPTIONS: 'Not one of the choices',
      NOT_A_LIST: 'Must be a list of choices',
      HTTPS_REQUIRED: 'Must start with https://',
      ADDRESS_NOT_ALLOWED: 'This address is not allowed (local or private network)',
      INVALID_URL: 'Not a valid web address',
      URL_CREDENTIALS_FORBIDDEN: 'The address must not contain a user name or password',
      URL_QUERY_FORBIDDEN: 'The address must not contain ? or #',
      INVALID_PATH: 'Not a path starting with /',
      OUT_OF_RANGE: 'Out of range',
      NOT_A_SWITCH: 'Must be on or off',
      INVALID_TIME: 'Not a time (HH:MM)',
      INVALID_EMAIL: 'Not an e-mail address',
      INVALID_PHONE: 'Not a phone number with country code',
      INVALID_KEY: 'Use lower-case letters, digits and "-" (at most 40)',
      SECRET_REQUIRED: 'Required: enter the value or a reference',
      SECRET_TOO_LONG: 'Too long',
      NOTHING_TO_KEEP: 'There is no stored value to keep: enter one',
      ENV_REFERENCE_FORBIDDEN_IN_PRODUCTION: 'Server environment variables cannot be used on the production server',
      INVALID_SECRET_REFERENCE: 'Not a valid reference (env:, file: or vault:)',
      DB_REFERENCE_NOT_ALLOWED: 'Enter the value itself instead of a db: reference',
      RESERVED_SECRET_REFERENCE: 'This reference is reserved',
      NOT_PRODUCTION_READY: 'This module cannot be switched on on the production server yet',
      // email/wildduck
      DUPLICATE_PLAN_CODE: 'Another plan already has this code',
      // hosting/cpanel
      TLS_VERIFICATION_REQUIRED: "The production server always verifies the WHM server's SSL certificate",
      PACKAGE_MAP_INVALID: 'One code=WHM package pair per plan, separated by commas; no code or package twice',
      // cloud/proxmox
      NOT_A_HOST: 'A host name or IP address only, without https://, a path or a port',
      NOT_A_TOKEN_ID: 'An API token id is USER@REALM!TOKENID, e.g. tino@pve!platform',
      NOT_NODE_NAMES: 'Node names of the cluster separated by commas, e.g. pve1, pve2',
      INVALID_FLAVORS: 'One size per entry, code=vCPUs/RAM MB/disk GB/transfer GB, separated by semicolons; no code twice',
      INVALID_IMAGES: 'One image per entry, code=template VMID/name/linux, windows or app/minimum disk GB, separated by semicolons',
      INVALID_ADDRESS_RANGE: 'First-last address of one family, both inside one network of the prefix length',
      GATEWAY_OUTSIDE_NETWORK: "The gateway is not in the range's network",
      ADDRESS_RANGE_REQUIRED: 'Give an IPv4 or an IPv6 range for the servers',
      other: 'Not accepted by the module ({{code}})',
    },
    warning: {
      title: 'Saved, with notes',
      PRIVATE_ADDRESS: 'The address is in a private network: only servers inside it can reach it.',
      PLAIN_HTTP: 'The address is not encrypted (http://).',
      other: 'Note from the module ({{code}})',
    },
    meta: 'Code {{code}} · request {{requestId}}',
  },
};
